import { type Grade, type LinkInternal, type Price } from '../../api-models'
import { type MobilePlanOffer } from '../../api-specs-b2c-services/mobile-plan/types/mobile-plan-offers'
import { createHttpEndpoint } from '../../utils'

/**
 * Returns a description of the product pickers to display on the product page
 * @see {@link https://devportal.backmarket.io/catalog/default/api/product-page-api/definition#tag/pickers/operation/getPickers}
 */
export const getPickers = createHttpEndpoint<GetPickersResponse>({
  method: 'GET',
  operationId: 'getPickers',
  path: '/bm/product/:productId/pickers',
})

export const getPickersV2 = createHttpEndpoint<GetPickersResponseV2>({
  method: 'GET',
  operationId: 'getPickers',
  path: '/product-page/products/:productId/pickers',
})

export type PickersResponseItem = {
  trackingValue?: string
  label: string
  link?: LinkInternal
  selected: boolean
  available: boolean
  acquirable: boolean
  icon?: 'IconEyes' | 'IconSparkles'
  price?: Price
  color?: string
  goodDeal?: boolean
  grade?: Grade
  specialOfferType?: {
    type: number
    name: string
  }
  productId: string
  emphasis?: 'info' | 'accent'
  extraData?: MobilePlanOffer
  sortingKey?: number | string | null
}

export type PickersResponseItemV2 = Omit<
  PickersResponseItem,
  | 'color'
  | 'emphasis'
  | 'grade'
  | 'specialOfferType'
  | 'icon'
  | 'link'
  | 'extraData'
> & {
  slug: string
  color: string | null
  parameters: {
    grade: Grade
    specialOfferType: {
      name: string
      value: number
    }
    mobilePlanOfferId: string | null
  }
  extraData: {
    mobilePlanOffer?: MobilePlanOffer
  } | null
}

export type Picker = {
  id: string
  trackingId?: string
  label: string
  items: Array<PickersResponseItem>
}

export type PickerV2 = {
  id: string
  trackingId?: string
  label: string
  items: Array<PickersResponseItemV2>
}

export type PickerWithExtraData = PickersResponseItem & {
  extraData: MobilePlanOffer
}

//  Extra data guard
export function hasItemExtraData(
  item: PickersResponseItem,
): item is PickerWithExtraData {
  return Boolean(item.extraData)
}

export type GetPickersResponse = {
  pickers: Array<Picker>
}

export type GetPickersResponseV2 = {
  isOutOfStock: boolean
  pickerGroups: Array<PickerV2>
}
